import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const COLORS = {
  cityBoundary: '#1F2937',
  congressional: '#EF4444',
  senate: '#3B82F6',
  house: '#10B981',
  school: '#F59E0B',
};

/**
 * Leaflet-based district map. Works for both place and address results.
 *
 * Props:
 *   placeFeature  — GeoJSON feature for place boundary (optional)
 *   districtData  — { congressional: [], senate: [], house: [], school: [] }
 *   marker        — { lat, lon } to place a pin (optional, for address results)
 *   height        — CSS height string
 *   onPointClick  — callback(lat, lon) when user clicks map
 *   onSelectedDistrictsChange — callback(selectedDistricts) when selection changes
 */
const PlaceMap = forwardRef(function PlaceMap(
  {
    placeFeature,
    districtData,
    marker,
    height = '400px',
    onPointClick,
    onSelectedDistrictsChange,
    frozen = false,
    defaultVisibleLayers = {},
  },
  ref
) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [visibleLayers, setVisibleLayers] = useState({
    cityBoundary: true,
    congressional: true,
    senate: true,
    house: true,
    school: false,
    ...defaultVisibleLayers,
  });
  const [selectedDistricts, setSelectedDistricts] = useState(null); // { congressional: [...], senate: [...], etc }
  const layersRef = useRef({});
  const featureLookupRef = useRef({});
  const shouldZoomRef = useRef(false);
  const visibleLayersRef = useRef(visibleLayers);
  const persistentTooltipsRef = useRef(null);

  // Expose selectedDistricts to parent via ref
  useImperativeHandle(ref, () => ({
    getSelectedDistricts: () => selectedDistricts,
  }));

  // Notify parent when selection changes
  useEffect(() => {
    if (onSelectedDistrictsChange) {
      onSelectedDistrictsChange(selectedDistricts);
    }
  }, [selectedDistricts, onSelectedDistrictsChange]);

  // Initialize map once on mount
  useEffect(() => {
    let isMounted = true;

    if (!window.L) {
      // Load CSS first
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.css';
      link.onload = () => {
        if (!isMounted) return;
        // Only load JS after CSS is ready
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.js';
        script.async = true;
        script.onload = () => {
          if (!isMounted) return;
          initializeMap();
        };
        script.onerror = () => {
          console.error('Failed to load Leaflet script');
          if (isMounted) setMapReady(false);
        };
        document.body.appendChild(script);
      };
      link.onerror = () => {
        console.error('Failed to load Leaflet CSS');
        if (isMounted) setMapReady(false);
      };
      document.head.appendChild(link);
    } else {
      initializeMap();
    }

    // Cleanup function
    return () => {
      isMounted = false;
      // Clean up map if it was created
      if (map.current) {
        try {
          map.current.off();
          map.current.remove();
          map.current = null;
        } catch (err) {
          console.error('Error cleaning up map:', err);
        }
      }
    };

    function initializeMap() {
      if (!mapContainer.current) {
        return;
      }
      if (map.current) {
        return;
      }

      // If the DOM container already has a Leaflet map attached (e.g. after
      // ErrorBoundary remount), tear it down cleanly before creating a new one.
      if (mapContainer.current._leaflet_id) {
        try {
          window.L.DomUtil.empty(mapContainer.current);
          delete mapContainer.current._leaflet_id;
        } catch (e) {
          // ignore — just ensure we don't double-initialize
        }
      }

      // Determine initial center (default US center)
      const centerLat = 39.8,
        centerLon = -98.5,
        zoom = 4;

      try {
        map.current = window.L.map(mapContainer.current, {
          doubleClickZoom: false,
          zoomAnimation: true,
          dragging: !frozen,
          touchZoom: !frozen,
          scrollWheelZoom: !frozen,
          boxZoom: !frozen,
          keyboard: !frozen,
          zoomControl: !frozen,
        }).setView([centerLat, centerLon], zoom);

        window.L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; CartoDB contributors',
          maxZoom: 19,
        }).addTo(map.current);
        setMapReady(true);
      } catch (err) {
        console.error('Error creating map:', err);
        return;
      }

      // Add marker pin if provided
      if (marker) {
        window.L.marker([marker.lat, marker.lon]).addTo(map.current);
      }

      // Helper function to find districts at a point
      function findDistrictsAtPoint(lat, lon) {
        const matching = {
          congressional: [],
          senate: [],
          house: [],
          school: [],
        };

        Object.entries(featureLookupRef.current).forEach(([type, features]) => {
          // Only include districts if their layer is visible
          if (!visibleLayersRef.current[type]) return;

          features.forEach((feature) => {
            if (pointInPolygon([lon, lat], feature.geometry)) {
              matching[type].push(feature);
            }
          });
        });

        return matching;
      }

      // Handle single click - show persistent tooltip
      function handleMapClick(lat, lon) {
        // Select districts at this location
        const matching = findDistrictsAtPoint(lat, lon);
        // Only set selection if we found visible districts at this point
        if (Object.values(matching).some((arr) => arr.length > 0)) {
          setSelectedDistricts(matching);

          // Show persistent tooltip at this location
          const districtTypes = [
            { key: 'congressional', label: 'US Congr. Dist' },
            { key: 'senate', label: 'State Sen. Dist' },
            { key: 'house', label: 'State House Dist' },
            { key: 'school', label: 'ISD' },
          ];

          const tooltipParts = [];
          districtTypes.forEach(({ key, label }) => {
            if (matching[key].length > 0) {
              const names = matching[key]
                .map(
                  (f) => f.properties?.BASENAME || f.properties?.NAME || f.properties?.name || key
                )
                .join(', ');
              tooltipParts.push(`${label}: ${names}`);
            }
          });

          // Remove old persistent tooltip if it exists
          if (persistentTooltipsRef.current) {
            map.current.removeLayer(persistentTooltipsRef.current);
          }

          // Create and show persistent tooltip
          if (tooltipParts.length > 0) {
            const tooltip = window.L.tooltip({
              permanent: true,
              sticky: false,
              className: 'persistent-tooltip',
            })
              .setLatLng([lat, lon])
              .setContent(tooltipParts.join('<br>'))
              .addTo(map.current);

            persistentTooltipsRef.current = tooltip;
          }

          if (onPointClick) onPointClick(lat, lon);
        }
      }

      // Handle double click - zoom to fit
      function handleMapDoubleClick() {
        // Only zoom if districts are already selected
        if (selectedDistricts && Object.values(selectedDistricts).some((arr) => arr.length > 0)) {
          shouldZoomRef.current = true;
        }
      }

      // Show hover tooltip on mousemove
      function updateHoverTooltip(lat, lon) {
        const matching = findDistrictsAtPoint(lat, lon);

        // Build tooltip text from all matching districts
        const tooltipParts = [];
        const districtTypes = [
          { key: 'congressional', label: 'US Congr Dist ', abbr: true },
          { key: 'senate', label: 'State Senate Dist', abbr: true },
          { key: 'house', label: 'State House Dist', abbr: true },
          { key: 'school', label: 'ISD', abbr: true },
        ];

        districtTypes.forEach(({ key, label }) => {
          if (matching[key].length > 0) {
            const names = matching[key]
              .map((f) => f.properties?.BASENAME || f.properties?.NAME || f.properties?.name || key)
              .join(', ');
            tooltipParts.push(`${label}: ${names}`);
          }
        });

        // Remove old tooltip if it exists
        if (map.current._hoverTooltip) {
          try {
            map.current.removeLayer(map.current._hoverTooltip);
          } catch (e) {
            console.warn('Error removing old tooltip:', e);
          }
          map.current._hoverTooltip = null;
        }

        // Create and show new tooltip
        if (tooltipParts.length > 0) {
          const tooltip = window.L.tooltip({
            permanent: false,
            sticky: true,
            className: 'multi-district-tooltip',
          })
            .setLatLng([lat, lon])
            .setContent(tooltipParts.join('<br>'))
            .addTo(map.current);

          map.current._hoverTooltip = tooltip;
        }
      }

      // Clear hover tooltip
      function clearHoverTooltip() {
        if (map.current && map.current._hoverTooltip) {
          try {
            map.current.removeLayer(map.current._hoverTooltip);
          } catch (e) {
            console.warn('Error clearing hover tooltip:', e);
          }
          map.current._hoverTooltip = null;
        }
      }

      // Track clicks for double-click detection
      let lastClickTime = 0;
      let lastClickLat = null;
      let lastClickLon = null;

      // Add click handler for point queries - detect single vs double click (unless frozen)
      if (!frozen) {
        map.current.on('click', (e) => {
          e.originalEvent.stopPropagation();
          const lat = e.latlng.lat;
          const lon = e.latlng.lng;
          const now = Date.now();

          // Check if this is a double-click (within 300ms and same location)
          const isDoubleClick =
            now - lastClickTime < 300 &&
            Math.abs(lat - (lastClickLat || 0)) < 0.0001 &&
            Math.abs(lon - (lastClickLon || 0)) < 0.0001;

          lastClickTime = now;
          lastClickLat = lat;
          lastClickLon = lon;

          if (isDoubleClick) {
            handleMapDoubleClick(lat, lon);
          } else {
            handleMapClick(lat, lon);
          }
        });

        // Add mousemove handler for multi-layer tooltip
        map.current.on('mousemove', (e) => {
          const lat = e.latlng.lat;
          const lon = e.latlng.lng;
          updateHoverTooltip(lat, lon);
        });

        // Clear tooltip on mouse leave
        map.current.on('mouseleave', () => {
          clearHoverTooltip();
        });
      }
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // Empty dependency array - initialize once on mount

  // Update layers and bounds when data changes
  useEffect(() => {
    if (!map.current) {
      return;
    }

    addLayers();

    // Fit to bounds — defer with requestAnimationFrame so the container is
    // visible and has real dimensions before Leaflet calculates bounds.
    requestAnimationFrame(() => {
      if (!map.current) return;
      map.current.invalidateSize();
      if (placeFeature?.geometry) {
        const latLngs = extractCoordinates(placeFeature.geometry).map(([lon, lat]) => [lat, lon]);
        if (latLngs.length > 0) map.current.fitBounds(latLngs, { padding: [20, 20] });
      } else if (hasAnyDistrictGeometry()) {
        fitToDistricts();
      }
    });

    function hasAnyDistrictGeometry() {
      return ['congressional', 'senate', 'house', 'school'].some(
        (key) => districtData?.[key]?.length > 0 && districtData[key][0]?.geometry
      );
    }

    function fitToDistricts() {
      const allCoords = [];
      ['congressional', 'senate', 'house', 'school'].forEach((key) => {
        const features = districtData?.[key] || [];
        features.forEach((f) => {
          if (f?.geometry) {
            allCoords.push(...extractCoordinates(f.geometry).map(([lon, lat]) => [lat, lon]));
          }
        });
      });
      if (allCoords.length > 0 && map.current) {
        map.current.fitBounds(allCoords, { padding: [20, 20] });
      }
    }

    function addLayers() {
      // Remove old layers from map
      Object.values(layersRef.current).forEach((layer) => {
        try {
          if (layer && map.current && map.current.hasLayer(layer)) {
            map.current.removeLayer(layer);
          }
        } catch (e) {
          // Layer already removed or invalid, skip
        }
      });

      // Clear previous lookup
      featureLookupRef.current = {};
      layersRef.current = {};

      // City boundary
      if (placeFeature?.geometry) {
        const cityLayer = window.L.geoJSON(placeFeature, {
          style: {
            color: COLORS.cityBoundary,
            weight: 3,
            opacity: 0.8,
            fillOpacity: 0.05,
          },
        });
        if (visibleLayers.cityBoundary) cityLayer.addTo(map.current);
        layersRef.current.cityBoundary = cityLayer;
      }

      // District layers
      const districtTypes = [
        { key: 'congressional', color: COLORS.congressional, label: 'US Congressional District' },
        { key: 'senate', color: COLORS.senate, label: 'State Senate District' },
        { key: 'house', color: COLORS.house, label: 'State House District' },
        { key: 'school', color: COLORS.school, label: 'School District' },
      ];

      districtTypes.forEach(({ key, color }) => {
        const features = districtData?.[key] || [];
        if (features.length === 0) return;

        // Track features for point-in-polygon lookup
        featureLookupRef.current[key] = features;

        const featureGroup = window.L.featureGroup();

        features.forEach((feature, idx) => {
          if (!feature?.geometry) return;

          const isSelected = selectedDistricts?.[key]?.some((f) => f === feature);

          const geoJsonLayer = window.L.geoJSON(feature, {
            style: {
              color,
              weight: isSelected ? 3 : 2,
              opacity: isSelected ? 1 : 0.6,
              fillOpacity: isSelected ? 0.4 : 0.15,
              dashArray: key === 'school' ? '4 4' : undefined,
            },
          });

          featureGroup.addLayer(geoJsonLayer);
        });

        if (visibleLayers[key]) featureGroup.addTo(map.current);
        layersRef.current[key] = featureGroup;
      });
    }
  }, [placeFeature, districtData, marker, visibleLayers, selectedDistricts, mapReady]);

  // Toggle layer visibility and keep ref in sync
  useEffect(() => {
    visibleLayersRef.current = visibleLayers;

    if (!map.current) return;

    Object.entries(visibleLayers).forEach(([key, visible]) => {
      const layer = layersRef.current[key];
      if (!layer) return;
      if (visible) {
        map.current.addLayer(layer);
      } else {
        map.current.removeLayer(layer);
      }
    });
  }, [visibleLayers]);

  // Update district styling when selection changes
  useEffect(() => {
    if (!map.current) return;

    // Redraw all district layers with updated styling
    const districtTypes = ['congressional', 'senate', 'house', 'school'];
    districtTypes.forEach((key) => {
      const featureGroup = layersRef.current[key];
      if (!featureGroup) return;

      // Clear and rebuild the feature group with updated styles
      featureGroup.clearLayers();

      const features = districtData?.[key] || [];
      const color = COLORS[key];

      features.forEach((feature) => {
        if (!feature?.geometry) return;

        const name =
          feature.properties?.BASENAME ||
          feature.properties?.NAME ||
          feature.properties?.name ||
          key;

        const isSelected = selectedDistricts?.[key]?.some((f) => f === feature);

        const geoJsonLayer = window.L.geoJSON(feature, {
          style: {
            color,
            weight: isSelected ? 3 : 2,
            opacity: isSelected ? 1 : 0.6,
            fillOpacity: isSelected ? 0.4 : 0.15,
            dashArray: key === 'school' ? '4 4' : undefined,
          },
        });

        featureGroup.addLayer(geoJsonLayer);
      });
    });
  }, [selectedDistricts, districtData]);

  // Zoom to fit selected districts on double-click
  useEffect(() => {
    if (!map.current || !selectedDistricts || !shouldZoomRef.current) return;

    const allCoords = [];
    Object.values(selectedDistricts).forEach((features) => {
      features.forEach((f) => {
        if (f?.geometry) {
          allCoords.push(...extractCoordinates(f.geometry).map(([lon, lat]) => [lat, lon]));
        }
      });
    });

    if (allCoords.length > 0) {
      // Fit bounds without animation to avoid the zoom-in-then-out effect
      map.current.fitBounds(allCoords, {
        padding: [30, 30],
        animate: false,
      });
      shouldZoomRef.current = false; // Reset flag after zooming
    }
  }, [selectedDistricts]);

  const toggleLayer = (layer) => {
    setVisibleLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  const layerConfig = [
    ...(placeFeature ? [{ key: 'cityBoundary', label: 'City Boundary', emoji: '🏙️' }] : []),
    { key: 'congressional', label: 'Congressional', emoji: '🏛️' },
    { key: 'senate', label: 'State Senate', emoji: '📜' },
    { key: 'house', label: 'State House', emoji: '🏢' },
    { key: 'school', label: 'School District', emoji: '🎓' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height }}>
      {!mapReady && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f4f6',
            borderRadius: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <p style={{ color: '#6b7280' }}>Loading map...</p>
        </div>
      )}
      <div
        ref={mapContainer}
        style={{
          flex: 1,
          borderRadius: '0.75rem',
          overflow: 'hidden',
          marginBottom: '0.75rem',
          minHeight: '300px',
          cursor: 'crosshair',
          position: 'relative',
          zIndex: 0,
          display: mapReady ? 'block' : 'none',
        }}
        title="Click on the map to see districts at that location"
      />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {layerConfig.map(({ key, label, emoji }) => {
          const hasData =
            key === 'cityBoundary' ||
            (districtData?.[key] && districtData[key].length > 0 && districtData[key][0]?.geometry);

          return (
            <button
              key={key}
              onClick={() => toggleLayer(key)}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: `2px solid ${COLORS[key]}`,
                backgroundColor: visibleLayers[key] ? COLORS[key] : 'transparent',
                color: visibleLayers[key] ? 'white' : COLORS[key],
                fontSize: '0.75rem',
                fontWeight: '600',
                cursor: hasData ? 'pointer' : 'default',
                transition: 'all 0.2s',
                opacity: hasData ? 1 : 0.3,
              }}
              disabled={!hasData}
            >
              {emoji} {label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default PlaceMap;

function getBoundingBox(geometry) {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  function processCoords(coords) {
    if (Array.isArray(coords[0])) {
      coords.forEach(processCoords);
    } else {
      const [x, y] = coords;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (geometry.type === 'Point') {
    const [x, y] = geometry.coordinates;
    return { minX: x - 0.1, minY: y - 0.1, maxX: x + 0.1, maxY: y + 0.1 };
  }

  processCoords(geometry.coordinates);
  return { minX, minY, maxX, maxY };
}

function extractCoordinates(geometry) {
  const coords = [];

  function process(arr) {
    if (Array.isArray(arr[0]) && Array.isArray(arr[0][0])) {
      arr.forEach(process);
    } else if (Array.isArray(arr[0]) && typeof arr[0][0] === 'number') {
      coords.push(...arr);
    }
  }

  process(geometry.coordinates);
  return coords;
}

// Ray-casting algorithm for point-in-polygon test
function pointInPolygon(point, geometry) {
  const [lon, lat] = point;
  const coords = geometry.coordinates;

  // Handle different geometry types
  if (geometry.type === 'Polygon') {
    return isPointInRing(point, coords[0]);
  } else if (geometry.type === 'MultiPolygon') {
    return coords.some((polygon) => isPointInRing(point, polygon[0]));
  }
  return false;
}

function isPointInRing(point, ring) {
  const [lon, lat] = point;
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}
